import * as React from "react";
import {
  getAllSystemTypes,
  getAllSystemsBySystemTypeID,
  getAllSubSystemsBySystemTypeID,
  addSystem,
  editSystem,
} from "services/centerPointUserServices";
import moment from "moment";
import BackdropComponent from "components/Spinner/Spinner";
import Grid from "@mui/material/Grid";
import classes from "./SystemCredentials.module.css";
import {
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import TextLabel from "components/Common/TextLabel/TextLabel";
import { Clear, Close } from "@mui/icons-material";
import DateField from "components/Common/DateField";
import { CustomButton } from "components/RosterConfig/RosterConfigStyle";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";

const SystemCredentials = (props) => {
  const [loader, setLoader] = React.useState(false);
  const [effectiveDate, setEffectiveDate] = React.useState(
    moment().format("YYYY-MM-DD"),
  );
  const [expiryDate, setExpiryDate] = React.useState("2099-12-31");
  const [subSystemId, setSubSystemId] = React.useState(0);
  const [error, setError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [subSystemArray, setSubSystemArray] = React.useState([]);
  const [systemTypesArray, setSystemTypesArray] = React.useState([]);
  const [systemTypeId, setSystemTypeId] = React.useState(0);
  const [system, setSystem] = React.useState("");
  const [systemsArray, setSystemsArray] = React.useState([]);
  const [logonId, setLogonId] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        await fetchData();

        if (!cancelled && props?.addOrEdit === "Edit") {
          setEffectiveDate(props?.dataToBeEdited["effectiveDate"]);
          setExpiryDate(props?.dataToBeEdited["expireDate"]);
          setLogonId(props?.dataToBeEdited["logonId"]);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch data:", error);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, [props.dataToBeEdited]);

  const fetchData = async () => {
    setLoader(true);
    try {
      const systemTypesResponse = await getAllSystemTypes();
      if (systemTypesResponse?.data?.message === "Success") {
        setSystemTypesArray(systemTypesResponse?.data?.data);
        if (props?.addOrEdit === "Edit") {
          setSystemTypeId(props?.dataToBeEdited?.SystemTypeId);
          handleSystemTypeChange(
            props?.dataToBeEdited?.SystemTypeId,
            "initialLoad",
          );
        }
      } else {
        props?.errorHandler(true, "Could not fetch system types", "error");
      }
    } catch (error) {
      props?.errorHandler(true, "Something went wrong !", "error");
    }

    if (props?.addOrEdit !== "Edit") setLoader(false);
  };

  const handleSystemTypeChange = async (systemTypeId, mode) => {
    setLoader(true);
    setError(false);
    setSystemTypeId(() => systemTypeId);

    try {
      const response = await getAllSystemsBySystemTypeID(systemTypeId);
      if (response?.data?.message === "Success") {
        setSystemsArray(() => response?.data?.data);
        if (props.addOrEdit === "Edit" && mode === "initialLoad") {
          handleSystemChange(props?.dataToBeEdited?.SystemId, "initialLoad");
          setSystem(() => props?.dataToBeEdited?.SystemId);
        } else {
          setSystem("");
          setSubSystemId("");
          setSubSystemArray([]);
        }
      } else {
        props?.errorHandler(true, "Could not fetch systems", "error");
      }
    } catch (error) {
      props?.errorHandler(true, "Something went wrong !", "error");
    }
    if (props?.addOrEdit === "Add" || mode === "onChange") setLoader(false);
  };

  const handleSystemChange = async (CP_SystemId, mode) => {
    setLoader(true);
    setError(false);
    setSystem(CP_SystemId);
    try {
      const response = await getAllSubSystemsBySystemTypeID(
        props.addOrEdit === "Edit" && mode === "initialLoad"
          ? props?.dataToBeEdited?.SystemTypeId
          : systemTypeId,
        CP_SystemId,
      );
      if (response?.data?.message === "Success") {
        setSubSystemArray(response?.data?.data);
        if (props?.addOrEdit && mode === "initialLoad")
          setSubSystemId(props?.dataToBeEdited?.SubSystemId);
      } else {
        props?.errorHandler(true, "Could not fetch sub systems", "error");
      }
    } catch (error) {
      props?.errorHandler(true, "Something went wrong !", "error");
    }

    setLoader(false);
  };

  const add = async (payload) => {
    try {
      setError(false);
      setErrorMessage("");

      const response = await addSystem(payload);
      if (response?.data?.data[0]?.IsError === 0) {
        props?.fetchData();
        props?.handleCancelAddOrEdit();
        props?.errorHandler(true, "System Credential Added", "success");
      } else {
        setError(true);
        setErrorMessage(response?.data?.data[0]?.ErrorMessage);
      }
    } catch (error) {
      props?.errorHandler(true, "Something went wrong", "error");
    }
    setLoader(false);
  };

  const edit = async (payload) => {
    try {
      setError(false);
      setErrorMessage("");

      const response = await editSystem(payload);
      if (response?.data?.data[0]?.IsError === 0) {
        props?.fetchData();
        props?.handleCancelAddOrEdit();
        props?.errorHandler(true, "System Credential Edited", "success");
      } else {
        setError(true);
        setErrorMessage(response?.data?.data[0]?.ErrorMessage);
      }
    } catch (error) {
      props?.errorHandler(true, "Something went wrong", "error");
    }
    setLoader(false);
  };

  const handleAddOrEditSystem = async () => {
    const effectiveMoment = moment(effectiveDate);
    const expiryMoment = moment(expiryDate);

    if (!expiryDate) {
      setError(true);
      setErrorMessage("Expiry Date is mandatory");
    } else if (!systemTypeId) {
      setError(true);
      setErrorMessage("System Type is mandatory");
    } else if (!subSystemId) {
      setError(true);
      setErrorMessage("Sub system is mandatory.");
    } else if (!logonId) {
      setError(true);
      setErrorMessage("Logon Id is mandatory");
    } else if (effectiveDate && !expiryMoment.isAfter(effectiveMoment)) {
      setError(true);
      setErrorMessage("Expiry Date should be greater than effective date");
    } else {
      setLoader(true);
      const payload = {
        loggedOnUserName: localStorage.getItem("User"),
        cpEmployeeId: Number(props?.employeeId) || 0,
        systemTypeId: Number(systemTypeId),
        ...(props?.addOrEdit === "Edit"
          ? { employeeSystemId: props?.dataToBeEdited?.EmployeeSystemId }
          : {}),
        systemId: Number(system) || 0,
        subSystemId: Number(subSystemId),
        logOnId: logonId,
        effectiveDate: effectiveDate,
        expireDate: expiryDate,
      };
      if (props?.addOrEdit === "Add") add(payload);
      else edit(payload);
    }
  };

  return (
    <>
      {loader ? <BackdropComponent /> : null}
      <Grid container>
        <Grid item xs={11.5} sx={{ p: 2 }}>
          <TextLabel
            text={`${props?.addOrEdit} System Credentials`}
            header
            fontSize={16}
          ></TextLabel>
        </Grid>
        <Grid item xs={0.5} sx={{ pt: 2 }}>
          <IconButton onClick={props.handleCancelAddOrEdit}>
            <Close sx={{ color: "#3299ff" }} />
          </IconButton>
        </Grid>
        <Grid item xs={4} sx={{ p: 2 }}>
          <Grid container>
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  letterSpacing: "0.10rem",
                  fontWeight: "bold",
                  color: "#3F5765",
                  paddingBottom: 1,
                }}
              >
                {"System Type"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <Select
                  value={systemTypeId}
                  size="small"
                  onChange={(e) =>
                    handleSystemTypeChange(e.target.value, "onChange")
                  }
                  disabled={props?.addOrEdit === "Edit"}
                >
                  {systemTypesArray.map((systemTypes, index) => (
                    <MenuItem
                      key={systemTypes?.CP_SystemTypeId}
                      value={systemTypes?.CP_SystemTypeId}
                    >
                      {systemTypes?.Name} ({systemTypes?.Code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4} sx={{ p: 2 }}>
          <Grid container>
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  letterSpacing: "0.10rem",
                  fontWeight: "bold",
                  color: "#3F5765",
                  paddingBottom: 1,
                }}
              >
                {"Systems"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <Select
                  value={system}
                  size="small"
                  onChange={(e) =>
                    handleSystemChange(e.target.value, "onChange")
                  }
                  disabled={props?.addOrEdit === "Edit"}
                >
                  {systemsArray.map((systemLocation, index) => (
                    <MenuItem
                      key={systemLocation?.CP_SystemId}
                      value={systemLocation?.CP_SystemId}
                    >
                      {systemLocation?.Name} ({systemLocation?.Code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4} sx={{ p: 2 }}>
          <Grid container>
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  letterSpacing: "0.10rem",
                  fontWeight: "bold",
                  color: "#3F5765",
                  paddingBottom: 1,
                }}
              >
                {"Subsystem"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <Select
                  value={subSystemId}
                  size="small"
                  onChange={(e) => {
                    setError(false);
                    setSubSystemId(e.target.value);
                  }}
                  disabled={props?.addOrEdit === "Edit"}
                >
                  {subSystemArray.map((subSystem, index) => (
                    <MenuItem
                      key={subSystem?.CP_SubSystemId}
                      value={subSystem?.CP_SubSystemId}
                    >
                      {subSystem?.SubSystemName} ({subSystem?.SubSystemCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4} sx={{ p: 2 }}>
          <Grid container>
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  letterSpacing: "0.10rem",
                  fontWeight: "bold",
                  color: "#3F5765",
                  paddingBottom: 1,
                }}
              >
                {"Logon ID"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                onChange={(e) => {
                  setError(false);
                  setLogonId(() => e.target.value);
                }}
                value={logonId}
                disabled={props?.addOrEdit === "Edit"}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4} sx={{ p: 2 }}>
          <Grid container>
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  letterSpacing: "0.10rem",
                  fontWeight: "bold",
                  color: "#3F5765",
                  paddingBottom: 1,
                }}
              >
                {"Effective Date"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={effectiveDate === "" ? null : moment(effectiveDate)}
                  onChange={(newValue) => {
                    if (
                      newValue &&
                      newValue instanceof Date &&
                      !isNaN(newValue)
                    ) {
                      setError(false);
                      setEffectiveDate(format(newValue, "yyyy-MM-dd"));
                    }
                  }}
                  format="yyyy-MM-dd"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      error={false}
                      fullWidth
                      size="small"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {effectiveDate && (
                              <Tooltip title={"Clear"}>
                                <IconButton
                                  onClick={() => setEffectiveDate("")}
                                  edge="end"
                                >
                                  <Clear />
                                </IconButton>
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4} sx={{ p: 2 }}>
          <Grid container>
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  letterSpacing: "0.10rem",
                  fontWeight: "bold",
                  color: "#3F5765",
                  paddingBottom: 1,
                }}
              >
                {"Expire Date"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={expiryDate === "" ? null : moment(expiryDate)}
                  onChange={(newValue) => {
                    if (
                      newValue &&
                      newValue instanceof Date &&
                      !isNaN(newValue)
                    ) {
                      setError(false);
                      setExpiryDate(format(newValue, "yyyy-MM-dd"));
                    }
                  }}
                  format="yyyy-MM-dd"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      error={false}
                      fullWidth
                      size="small"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {expiryDate && (
                              <Tooltip title={"Clear"}>
                                <IconButton
                                  onClick={() => setExpiryDate("")}
                                  edge="end"
                                >
                                  <Clear />
                                </IconButton>
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={3} sx={{ p: 2 }}></Grid>
        <Grid item xs={6} sx={{ p: 2 }}>
          {error && <p className={classes.error}>{errorMessage}</p>}
        </Grid>
        <Grid item xs={3} sx={{ p: 2 }}></Grid>

        <Grid item xs={1} sx={{ pl: 2, pb: 1 }}>
          <CustomButton
            variant="contained"
            sx={{ backgroundColor: "#3299FF" }}
            onClick={handleAddOrEditSystem}
            disabled={error}
          >
            {props.addOrEdit === "Add" ? "Save" : "Update"}
          </CustomButton>
        </Grid>
        <Grid item xs={1} sx={{ pl: 2, pb: 1 }}>
          <CustomButton
            variant="outlined"
            sx={{ color: "#3299FF" }}
            onClick={props?.handleCancelAddOrEdit}
          >
            Cancel
          </CustomButton>
        </Grid>
        <Grid item xs={5}></Grid>
        <Grid item xs={5}></Grid>
      </Grid>
    </>
  );
};

SystemCredentials.propTypes = {
  dataToBeEdited: PropTypes.object.isRequired,
  addOrEdit: PropTypes.string.isRequired,
  handleCancelAddOrEdit: PropTypes.func.isRequired,
  errorHandler: PropTypes.func.isRequired,
};

export default SystemCredentials;
